import { css, IF, IS_DEFINED, NO, UIComponentView, UITextField, UIView, YES } from "uicore-ts"
import { ValueOf } from "uicore-ts/compiledScripts/UIObject"
import { UITextView } from "uicore-ts/compiledScripts/UITextView"
import { RHColor } from "./RHColor"
import { RHForms } from "./Utils_slop/RHDesignTokens"
import { RHNumberInputValidation } from "./Utils_slop/RHNumberInputValidation"


const RH_TEXT_FIELD_TEXT_ELEMENT_STYLE_CLASS_NAME = "RHTextFieldTextElement"

UIView.injectCSS(css`
    .${RH_TEXT_FIELD_TEXT_ELEMENT_STYLE_CLASS_NAME}::placeholder {
        color: ${RHForms.textFieldPlaceholderColor} !important;
        -webkit-text-fill-color: ${RHForms.textFieldPlaceholderColor} !important;
        opacity: 1;
        user-select: none;
    }
`, "rh-textfield-placeholder")


@UIComponentView
export class RHTextField extends UITextField {
    
    // --- Validation Configuration ---
    
    isRequired: boolean = false
    validationRegex?: RegExp
    validateOnInput: boolean = false
    validateOnBlur: boolean = true
    protected hasBeenBlurred: boolean = false
    _hasValidated = NO
    
    
    // --- Numeric Stepper Configuration ---
    
    private _type: "text" | "number" | "tel" | "email" | "password" = "text"
    private _minValue?: number
    private _maxValue?: number
    
    // --- Numeric Thousands Separator Display Overlay ---
    
    /**
     * A plain <span> element that floats over the <input> when the field is
     * unfocused and a thousandsSeparator is configured. It shows the formatted
     * value while the native input (with type="number") retains its raw value
     * and all its native behaviour (up/down arrows, mobile numeric keypad, etc.).
     *
     * Lifecycle:
     *   - Created lazily the first time thousandsSeparator is set to a non-null value.
     *   - Shown on blur, hidden on focus unless showsFormattedDisplayWhileFocused is enabled.
     *   - Destroyed when thousandsSeparator is set back to null.
     */
    _formattedDisplayElement: HTMLSpanElement | null = null
    _showsFormattedDisplayWhileFocused: boolean = NO
    
    set showsFormattedDisplayWhileFocused(value: boolean) {
        this._showsFormattedDisplayWhileFocused = value
        if (value == YES) {
            this._showFormattedDisplay()
        }
    }
    
    get showsFormattedDisplayWhileFocused(): boolean {
        return this._showsFormattedDisplayWhileFocused
    }
    
    override set thousandsSeparator(value: string | null) {
        super.thousandsSeparator = value
        if (value !== null) {
            this._initFormattedDisplayElementIfNeeded()
            this._showFormattedDisplay()
        }
        else {
            this._destroyFormattedDisplayElement()
        }
    }
    
    override get thousandsSeparator(): string | null {
        return super.thousandsSeparator
    }
    
    _initFormattedDisplayElementIfNeeded() {
        if (this._formattedDisplayElement) {
            return
        }
        
        const span = document.createElement("span")
        
        span.style.cssText = [
            "position: absolute",
            "inset: 0",
            "display: flex",
            "align-items: center",
            "pointer-events: none",
            "background: transparent",
            `padding: ${RHForms.textFieldContentPadding}`,
            "margin: 0",
            "overflow: hidden",
            "white-space: nowrap",
            "text-overflow: ellipsis"
        ].join("; ")
        
        this.viewHTMLElement.style.position = "absolute"
        this.viewHTMLElement.appendChild(span)
        this._formattedDisplayElement = span
    }
    
    _destroyFormattedDisplayElement() {
        if (this._formattedDisplayElement) {
            this._formattedDisplayElement.remove()
            this._formattedDisplayElement = null
        }
        this.viewHTMLElement.style.position = ""
    }
    
    _updateFormattedDisplay() {
        if (!this._formattedDisplayElement || this.thousandsSeparator === null) {
            return
        }
        const raw = this.textElementView.viewHTMLElement.value
        if (raw === "") {
            this._formattedDisplayElement.textContent = ""
            return
        }
        if (!RHNumberInputValidation.isValidNumberTextWithinRange(
            raw,
            this.allowDecimals,
            this.minValue,
            this.maxValue
        )) {
            this._formattedDisplayElement.textContent = raw
            return
        }
        // The thousands-separator formatter expects a period as the decimal
        // separator. Normalise the raw value before formatting, then substitute
        // the comma back into the result if we are in comma mode.
        const normalisedRaw = this._normaliseDecimalSeparator(raw)
        const formattedWithPeriod = UITextView.applyThousandsSeparatorToNumericalString(normalisedRaw, this.thousandsSeparator)
        this._formattedDisplayElement.textContent = IF(this._usesCommaAsDecimalSeparator)(() =>
            formattedWithPeriod.replace(".", ",")
        ).ELSE(() =>
            formattedWithPeriod
        )
    }
    
    _showFormattedDisplay() {
        if (!this._formattedDisplayElement) {
            return
        }
        this._updateFormattedDisplay()
        this._formattedDisplayElement.style.display = "flex"
        if (this.textElementView.viewHTMLElement.value === "") {
            // Empty field: reset the fill-color so the gray ::placeholder shows through.
            // Forcing it to textBlack here would also paint the placeholder black (WebKit
            // applies -webkit-text-fill-color to placeholder text), making it look like a
            // typed value rather than a hint.
            this.textElementView.viewHTMLElement.style.color = ""
            this.textElementView.viewHTMLElement.style.setProperty(
                "-webkit-text-fill-color",
                ""
            )
            this.textElementView.viewHTMLElement.style.caretColor = ""
            return
        }
        // Make the native input text invisible — the value remains intact and
        // parseable; only the rendering is suppressed.
        this.textElementView.viewHTMLElement.style.color = "transparent"
        this.textElementView.viewHTMLElement.style.setProperty("-webkit-text-fill-color", "transparent")
        this.textElementView.viewHTMLElement.style.caretColor = RHColor.textBlack.stringValue
    }
    
    _hideFormattedDisplay() {
        if (!this._formattedDisplayElement) {
            return
        }
        this._formattedDisplayElement.style.display = "none"
        this.textElementView.viewHTMLElement.style.color = ""
        this.textElementView.viewHTMLElement.style.setProperty("-webkit-text-fill-color", RHColor.textBlack.stringValue)
        this.textElementView.viewHTMLElement.style.caretColor = ""
    }
    
    set type(value: "text" | "number" | "tel" | "email" | "password") {
        this._type = value
        this.textElementView.viewHTMLElement.setAttribute("type", value)
        if (value === "number" && !this._usesCommaAsDecimalSeparator) {
            this._usesCommaAsDecimalSeparator = YES
            this._installCommaInputFilter()
        }
        else if (value !== "number" && this._usesCommaAsDecimalSeparator) {
            this._usesCommaAsDecimalSeparator = NO
            this._removeCommaInputFilter()
        }
        this.updateNativeAttributes()
    }
    
    get type(): "text" | "number" | "tel" | "email" | "password" {
        return this._type
    }
    
    // This property is now read-only and derived, as we rely on native behavior
    get isNumericStepper(): boolean {
        return this._type === "number"
    }
    
    stepValue: number = 1
    
    set minValue(minValue: number | undefined) {
        this._minValue = minValue
        this.updateNativeAttributes()
        this.enforceMinMaxValuesIfNeeded()
    }
    
    get minValue(): number | undefined {
        return this._minValue
    }
    
    set maxValue(maxValue: number | undefined) {
        this._maxValue = maxValue
        this.updateNativeAttributes()
        this.enforceMinMaxValuesIfNeeded()
    }
    
    get maxValue(): number | undefined {
        return this._maxValue
    }
    
    enforceMinMaxValuesIfNeeded() {
        if (this.type !== "number") { return }
        // Do not touch an empty field — the user hasn't entered anything yet and
        // clamping would write e.g. "0" into a blank price input on initial setup.
        if (this.text.trim().length === 0) { return }
        const normalisedText = this._normaliseDecimalSeparator(this.text)
        let currentValue = normalisedText.numericalValue ?? 0
        if (IS_DEFINED(this.minValue)) {
            currentValue = [currentValue, this.minValue].max()
        }
        if (IS_DEFINED(this.maxValue)) {
            currentValue = [currentValue, this.maxValue].min()
        }
        // Restore comma separator if in comma mode
        const normalisedResult = currentValue + ""
        this.text = IF(this._usesCommaAsDecimalSeparator)(() =>
            normalisedResult.replace(".", ",")
        ).ELSE(() =>
            normalisedResult
        )
    }
    
    private _allowDecimals: boolean = false
    
    get allowDecimals(): boolean {
        return this._allowDecimals
    }
    
    set allowDecimals(value: boolean) {
        this._allowDecimals = value
        this.updateNativeAttributes()
    }
    
    // --- Comma Decimal Separator (Estonian locale) ---
    
    /**
     * When YES, the field accepts a comma as the decimal separator (e.g. "1,5").
     * Only meaningful when `type` is "number". The underlying HTML input is
     * switched to type="text" with inputmode="decimal" so the browser accepts
     * the comma character, while the field still behaves logically as a numeric
     * input. Min/max enforcement and formatted display work transparently.
     *
     * Consumers that need a JS number should read `numericValue` instead of
     * parsing `this.text` themselves.
     */
    private _usesCommaAsDecimalSeparator: boolean = NO
    
    get usesCommaAsDecimalSeparator(): boolean {
        return this._usesCommaAsDecimalSeparator
    }
    
    set usesCommaAsDecimalSeparator(value: boolean) {
        this._usesCommaAsDecimalSeparator = value
        this.updateNativeAttributes()
        if (value) {
            this._installCommaInputFilter()
        }
        else {
            this._removeCommaInputFilter()
        }
    }
    
    /**
     * Returns the current text value parsed as a JS number.
     * Handles both comma and period decimal separators transparently.
     * Returns NaN if the field is empty or the value is not a valid number.
     */
    get numericValue(): number {
        const numericValue = RHNumberInputValidation.numericValueFromTextWithinRange(
            this.text,
            this.allowDecimals,
            this.minValue,
            this.maxValue
        )
        if (numericValue === undefined) {
            return Number.NaN
        }
        return numericValue
    }
    
    private _commaInputFilterHandler: ((event: KeyboardEvent) => void) | null = null
    
    private _normaliseDecimalSeparator(text: string): string {
        return text.replace(",", ".")
    }
    
    private _installCommaInputFilter() {
        if (this._commaInputFilterHandler) {
            return
        }
        const handler = (event: KeyboardEvent) => {
            this._handleCommaDecimalKeydown(event)
        }
        this._commaInputFilterHandler = handler
        this.textElementView.viewHTMLElement.addEventListener("keydown", handler)
    }
    
    private _removeCommaInputFilter() {
        if (!this._commaInputFilterHandler) {
            return
        }
        this.textElementView.viewHTMLElement.removeEventListener("keydown", this._commaInputFilterHandler)
        this._commaInputFilterHandler = null
    }
    
    private _handleCommaDecimalKeydown(event: KeyboardEvent) {
        const key = event.key
        
        // Always allow: navigation, editing, and control keys
        if (
            key === "Backspace" ||
            key === "Delete" ||
            key === "ArrowLeft" ||
            key === "ArrowRight" ||
            key === "ArrowUp" ||
            key === "ArrowDown" ||
            key === "Tab" ||
            key === "Enter" ||
            key === "Home" ||
            key === "End" ||
            event.ctrlKey ||
            event.metaKey
        ) {
            return
        }
        
        const inputElement = this.textElementView.viewHTMLElement as HTMLInputElement
        const currentText = inputElement.value
        
        // Allow digits
        if (key >= "0" && key <= "9") {
            return
        }
        
        // Non-negative fields must not accept a minus sign.
        if (key === "-") {
            if (
                (!IS_DEFINED(this.minValue) || this.minValue < 0) &&
                inputElement.selectionStart === 0 &&
                !currentText.includes("-")
            ) {
                return
            }
            event.preventDefault()
            return
        }
        
        // Allow comma (or period as alias) as decimal separator only if decimals are
        // enabled and no separator exists outside the current selection.
        if (key === "," || key === ".") {
            if (this._allowDecimals) {
                const start = inputElement.selectionStart ?? currentText.length
                const end = inputElement.selectionEnd ?? currentText.length
                const textOutsideSelection = currentText.slice(0, start) + currentText.slice(end)
                const separatorAlreadyPresent = textOutsideSelection.includes(",") || textOutsideSelection.includes(".")
                if (!separatorAlreadyPresent) {
                    if (key === ".") {
                        // Intercept period and insert a comma instead.
                        event.preventDefault()
                        const newText = currentText.slice(0, start) + "," + currentText.slice(end)
                        inputElement.value = newText
                        inputElement.setSelectionRange(start + 1, start + 1)
                        this.sendControlEventForKey(UITextField.controlEvent.TextChange)
                    }
                    return
                }
            }
            event.preventDefault()
            return
        }

        // Block everything else.
        event.preventDefault()
    }
    
    // --- Constants for styling ---
    private readonly defaultBorderColor = RHForms.textFieldBorderColor.stringValue
    
    // Removed subviews logic as we use native spinners now
    
    constructor(elementID?: string, viewHTMLElement?: null, type?: string | ValueOf<typeof UITextView.type>) {
        
        super(elementID, viewHTMLElement, type)
        this.textElementView.viewHTMLElement.classList.add(RH_TEXT_FIELD_TEXT_ELEMENT_STYLE_CLASS_NAME)
        
        // Ensure that element has the correct class applied
        this.viewHTMLElement.classList.add(this.styleClassName)
        
        if (type === "text" || type === "number" || type === "tel" || type === "email" || type === "password") {
            this._type = type
        }
        
        // Initialize native attributes based on initial type
        this.updateNativeAttributes();
        
        // 3. Base Bootstrap Styling
        (this as RHTextField).configureWithObject({
            // Style the inner text element to be transparent and fill the container
            textElementView: {
                style: {
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    padding: "0",
                    margin: "0",
                    boxShadow: "none",
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    lineHeight: "inherit",
                    color: "inherit"
                }
            },
            // Style the outer container with Bootstrap styling
            textColor: RHColor.textBlack,
            backgroundColor: RHColor.textWhite,
            style: {
                display: "flex",  // Use flex for vertical centering
                alignItems: "center",  // Vertically center the text element
                width: "100%",
                // padding: "0.375rem 0.75rem",
                padding: RHForms.textFieldContentPadding,
                fontFamily: RHForms.textFieldFontFamily,
                // fontSize: "1rem",
                fontSize: RHForms.textFieldFontSize,
                // fontWeight: "400",
                fontWeight: RHForms.textFieldFontWeight,
                // lineHeight: "1.5",
                lineHeight: RHForms.textFieldLineHeight,
                backgroundClip: "padding-box",
                border: `1px solid ${this.defaultBorderColor}`,
                // borderRadius: "0.25rem",
                borderRadius: RHForms.textFieldBorderRadius,
                // transition: "border-color .15s ease-in-out,box-shadow .15s ease-in-out",
                transition: RHForms.textFieldTransition,
                boxSizing: "border-box",
                outline: "none",
                appearance: "none"
            }
        })
        
        // Forward focus/blur events from text element to container for visual effects
        this.textElementView.viewHTMLElement.addEventListener("focus", () => {
            this.sendControlEventForKey(UIView.controlEvent.Focus)
        })
        
        this.textElementView.viewHTMLElement.addEventListener("blur", () => {
            this.sendControlEventForKey(UIView.controlEvent.Blur)
        })
        
        // 4. Handle Focus State
        this.addTargetForControlEvent(UIView.controlEvent.Focus, () => {
            this.style.borderColor = RHColor.primaryBorderHover.stringValue
            this.style.boxShadow = `0 0 0 0.25rem ${RHColor.primaryShadow.stringValue}`
            this.style.outline = "0"
            if (this.showsFormattedDisplayWhileFocused == YES) {
                this._showFormattedDisplay()
            }
            else {
                this._hideFormattedDisplay()
            }
        })
        
        // 5. Handle Blur State
        this.addTargetForControlEvent(UIView.controlEvent.Blur, () => {
            this.hasBeenBlurred = true
            // We reset to default here before validate in case validate decides not to apply styles
            this.style.borderColor = this.defaultBorderColor
            this.style.boxShadow = "none"
            if (this.validateOnBlur) {
                this.validate()
            }
            this._showFormattedDisplay()
        })
        
        // 6. Handle Input Changes
        this.addTargetForControlEvent(UITextField.controlEvent.TextChange, () => {
            // If we are currently showing an error, clear it immediately on input
            if (this.style.borderColor === RHColor.danger.stringValue) {
                this.validate()
            }
            else if (this.validateOnInput && this.hasBeenBlurred) {
                this.validate()
            }
            // Keep the overlay in sync while the user types (it is hidden during
            // focus, but _updateFormattedDisplay caches the latest value so the
            // correct formatted string is shown immediately on blur).
            this._updateFormattedDisplay()
            if (this.showsFormattedDisplayWhileFocused == YES) {
                this._showFormattedDisplay()
            }
        })
        
        
        // 7. Placeholder Styling and Autofill handling
        if (this.elementID) {
            
            const containerElement = this.viewHTMLElement
            const textElement = this.textElementView?.viewHTMLElement
            
            if (containerElement && textElement instanceof HTMLInputElement) {
                
                const updateAutofillState = () => {
                    try {
                        if (textElement.matches(":-webkit-autofill")) {
                            containerElement.classList.add("is-autofilled")
                            const autofilledBackgroundColorString = this.textElementView.computedStyle.backgroundColor ?? "#e8f0fe"
                            UIView.injectCSS(css`
                                #${this.elementID}.is-autofilled {
                                    background-color: ${autofilledBackgroundColorString} !important;
                                }
                            `, ` rh-textfield-style-${this.elementID} `)
                        }
                        else {
                            containerElement.classList.remove("is-autofilled")
                        }
                    }
                    catch {
                        // Defensive: matches() can throw in rare browser edge cases
                    }
                }
                
                // Initial check (Chrome often autofills on page load)
                requestAnimationFrame(updateAutofillState)
                // The timeouts are a hack because sometimes the synchronous one does not work
                setTimeout(updateAutofillState, 10)
                setTimeout(updateAutofillState, 20)
                setTimeout(updateAutofillState, 50)
                setTimeout(updateAutofillState, 100)
                setTimeout(updateAutofillState, 200)
                setTimeout(updateAutofillState, 500)
                setTimeout(updateAutofillState, 1000)
                
                // Re-check on any meaningful interaction
                textElement.addEventListener("input", updateAutofillState)
                textElement.addEventListener("change", updateAutofillState)
                textElement.addEventListener("focus", updateAutofillState)
                textElement.addEventListener("blur", updateAutofillState)
            }
            
        }
    }
    
    /**
     * Updates the native HTML attributes (type, inputmode, min, max, step).
     *
     * When `usesCommaAsDecimalSeparator` is YES the DOM input type is forced to
     * "text" with inputmode="decimal" so the browser accepts comma characters.
     * Native min/max/step constraints are removed in that case; enforcement is
     * handled in JS via enforceMinMaxValuesIfNeeded instead.
     */
    private updateNativeAttributes() {
        if (this._type === "number") {
            if (this._usesCommaAsDecimalSeparator) {
                this.textElementView.viewHTMLElement.setAttribute("type", "text")
                this.textElementView.viewHTMLElement.setAttribute("inputmode", "decimal")
                this.textElementView.viewHTMLElement.removeAttribute("min")
                this.textElementView.viewHTMLElement.removeAttribute("max")
                this.textElementView.viewHTMLElement.removeAttribute("step")
            }
            else {
                this.textElementView.viewHTMLElement.setAttribute("type", "number")
                this.textElementView.viewHTMLElement.removeAttribute("inputmode")
                
                if (this._minValue !== undefined) {
                    this.textElementView.viewHTMLElement.setAttribute("min", this._minValue.toString())
                }
                else {
                    this.textElementView.viewHTMLElement.removeAttribute("min")
                }
                
                if (this._maxValue !== undefined) {
                    this.textElementView.viewHTMLElement.setAttribute("max", this._maxValue.toString())
                }
                else {
                    this.textElementView.viewHTMLElement.removeAttribute("max")
                }
                
                if (this.stepValue) {
                    this.textElementView.viewHTMLElement.setAttribute(
                        "step", this.allowDecimals ? "any" : this.stepValue.toString())
                }
            }
        }
        else {
            this.textElementView.viewHTMLElement.removeAttribute("min")
            this.textElementView.viewHTMLElement.removeAttribute("max")
            this.textElementView.viewHTMLElement.removeAttribute("inputmode")
        }
    }
    
    
    // --- Core Logic ---
    
    validate(): boolean {
        this._hasValidated = YES
        const currentValue = this.text ?? ""
        let isValid = true
        const hasNumericValueToValidate =
            this.type === "number" && currentValue.length > 0
        
        // Check if validation rules are actually configured
        const hasValidationRules = this.isRequired ||
            (this.validationRegex !== undefined) ||
            hasNumericValueToValidate
        
        if (!hasValidationRules) {
            // No rules defined: Return true (logically valid) but reset visuals to neutral
            this.resetValidationVisuals()
            return true
        }
        
        // Rules exist, perform validation logic
        if (this.isRequired && currentValue.length === 0) {
            isValid = false
        }
        
        if (isValid && currentValue.length > 0 && this.validationRegex) {
            if (!this.validationRegex.test(currentValue)) {
                isValid = false
            }
        }

        if (isValid && hasNumericValueToValidate) {
            isValid = RHNumberInputValidation.isValidNumberTextWithinRange(
                currentValue,
                this.allowDecimals,
                this.minValue,
                this.maxValue
            )
        }
        
        // Use the property setter
        this.isValid = isValid
        return isValid
    }
    
    resetValidationVisuals() {
        // Revert to the default state (gray border, no shadow)
        this.style.borderColor = this.defaultBorderColor
        this.style.boxShadow = "none"
    }

    resetValidationState() {
        this._hasValidated = NO
        this.hasBeenBlurred = NO
        this.resetValidationVisuals()
    }
    
    
    set isValid(value: boolean) {
        this._hasValidated = YES
        if (value) {
            this.style.borderColor = RHColor.success.stringValue
            this.style.boxShadow = "none"
        }
        else {
            this.style.borderColor = RHColor.danger.stringValue
            this.style.boxShadow = "none"
        }
    }
    
    
    override updateContentForCurrentEnabledState() {
        // UIView couples enabled to hidden; field visibility belongs to the form.
        
        if (this.enabled) {
            // --- ENABLED STATE ---
            this.alpha = 1
            this.textColor = RHColor.textBlack
            this.backgroundColor = RHColor.textWhite
            this.style.backgroundColor = RHColor.textWhite.stringValue
            this.textElementView.backgroundColor = this.backgroundColor
            this.textElementView.style.backgroundColor = "transparent"
            this.textElementView.style.color = RHColor.textBlack.stringValue
            this.textElementView.viewHTMLElement.style.backgroundColor = "transparent"
            this.textElementView.viewHTMLElement.style.backgroundImage = "none"
            this.textElementView.viewHTMLElement.style.boxShadow = "none"
            this.textElementView.viewHTMLElement.style.setProperty("-webkit-box-shadow", "none")
            this.textElementView.viewHTMLElement.style.setProperty("-webkit-text-fill-color", RHColor.textBlack.stringValue)
            
            if (this._hasValidated || this.hasBeenBlurred) {
                this.validate()
            }
            else {
                this.resetValidationVisuals()
            }
            this.enforceMinMaxValuesIfNeeded()
            
            this.style.cursor = "text"
        }
        else {
            // --- DISABLED STATE ---
            this.alpha = 1
            this.textColor = RHForms.textFieldDisabledTextColor
            this.backgroundColor = RHForms.textFieldDisabledBackgroundColor
            this.style.backgroundColor = RHForms.textFieldDisabledBackgroundColor.stringValue
            this.textElementView.backgroundColor = this.backgroundColor
            this.textElementView.style.backgroundColor = RHForms.textFieldDisabledBackgroundColor.stringValue
            this.textElementView.style.color = RHForms.textFieldDisabledTextColor.stringValue
            this.textElementView.viewHTMLElement.style.backgroundColor = RHForms.textFieldDisabledBackgroundColor.stringValue
            this.textElementView.viewHTMLElement.style.backgroundImage = "none"
            this.textElementView.viewHTMLElement.style.boxShadow = "none"
            this.textElementView.viewHTMLElement.style.setProperty(
                "-webkit-box-shadow",
                `0 0 0 1000px ${RHForms.textFieldDisabledBackgroundColor.stringValue} inset`
            )
            this.textElementView.viewHTMLElement.style.setProperty(
                "-webkit-text-fill-color",
                RHForms.textFieldDisabledTextColor.stringValue
            )
            
            this.blur()
            
            this.style.borderColor = RHForms.textFieldDisabledBorderColor.stringValue
            this.style.boxShadow = "none"
            this.style.cursor = "not-allowed"
        }
        
        
        if (this._formattedDisplayElement?.style.display === "flex") {
            this._showFormattedDisplay()
        }
        this.userInteractionEnabled = this.enabled
    }
    
    override intrinsicContentHeight(constrainingWidth = 0) {
        // Don't call super.intrinsicContentHeight() because its cache might have
        // been populated before styles were fully computed.
        // Instead, directly measure the text element height and add our padding/border.
        
        // Force a layout flush to ensure computed styles are available
        if (this.viewHTMLElement.isConnected) {
            this.viewHTMLElement.offsetHeight // Force reflow
        }
        
        // Get the text element's content height directly
        const textElementHeight = this.textElementView.viewHTMLElement.scrollHeight ||
            parseFloat(window.getComputedStyle(this.textElementView.viewHTMLElement).lineHeight) ||
            16
        
        // Get padding and border from computed styles
        const computed = window.getComputedStyle(this.viewHTMLElement)
        const paddingTop = parseFloat(computed.paddingTop) || 0
        const paddingBottom = parseFloat(computed.paddingBottom) || 0
        const borderTop = parseFloat(computed.borderTopWidth) || 0
        const borderBottom = parseFloat(computed.borderBottomWidth) || 0
        
        return textElementHeight + paddingTop + paddingBottom + borderTop + borderBottom
    }
    
}
